import { useCallback, useMemo } from 'react'
import { isFuture, format, isSameDay } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import type { DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

type Preset = 7 | 30

function getPresetRange(preset: Preset): { from: Date; to: Date } {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const from = new Date(today)
  from.setDate(from.getDate() - preset + 1)
  return { from, to: today }
}

interface DateRangePickerProps {
  dateRange: DateRange | undefined
  onDateRangeChange: (range: DateRange | undefined) => void
}

export function DateRangePicker({ dateRange, onDateRangeChange }: DateRangePickerProps) {
  const applyPreset = useCallback(
    (preset: Preset) => {
      onDateRangeChange(getPresetRange(preset))
    },
    [onDateRangeChange],
  )

  const activePreset = useMemo((): Preset | null => {
    if (!dateRange?.from || !dateRange?.to) return null
    const presets: Preset[] = [7, 30]
    for (const p of presets) {
      const range = getPresetRange(p)
      if (isSameDay(dateRange.from, range.from) && isSameDay(dateRange.to, range.to)) return p
    }
    return null
  }, [dateRange])

  return (
    <div className="flex items-center mb-2 gap-2">
      <label className="text-body font-medium whitespace-nowrap">Date Range:</label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-[300px] justify-start text-left font-normal">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, 'LLL dd, yyyy')} - {format(dateRange.to, 'LLL dd, yyyy')}
                </>
              ) : (
                format(dateRange.from, 'LLL dd, yyyy')
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={dateRange?.to}
            selected={dateRange}
            onSelect={onDateRangeChange}
            numberOfMonths={2}
            disabled={(date) => isFuture(date)}
          />
        </PopoverContent>
      </Popover>

      <div className="flex gap-1">
        <Button
          variant={activePreset === 7 ? 'default' : 'outline'}
          size="sm"
          className="h-7 px-2"
          onClick={() => applyPreset(7)}
        >
          Last 7d
        </Button>
        <Button
          variant={activePreset === 30 ? 'default' : 'outline'}
          size="sm"
          className="h-7 px-2"
          onClick={() => applyPreset(30)}
        >
          Last 30d
        </Button>
      </div>
    </div>
  )
}
